import React, {useMemo} from "react";
import Chart from "react-google-charts";
import {useTranslation} from "react-i18next";
import {CHART_BY_ID} from "./widgetCatalog";
import {formatCurrency} from "./formatters";
import {
    dailyRevenueSeries,
    dailyOrdersSeries,
    paymentMethodsSeries,
    revenueByHourSeries,
    revenueByPeriodSeries,
    heatmapMatrix,
    frequencyDistributionSeries,
    newVsReturning,
    topClientsRows,
} from "./chartData";
import ChartCard from "./ChartCard";
import {
    alignTimeSeries,
    mergeSeriesByLabel,
    COMPARISON_COLOR,
    comparisonLegend,
    dashedComparisonSeries,
} from "../comparison";

// ── Shared google-charts options ────────────────────────────────────────────
const baseOptions = (isDarkMode, extra = {}) => {
    const axisText = isDarkMode ? "#9CA3AF" : "#6B7280";
    const axisTitle = isDarkMode ? "#D1D5DB" : "#374151";
    return {
        hAxis: {textStyle: {fontSize: 11, color: axisText}, slantedText: true, slantedTextAngle: 45},
        vAxis: {minValue: 0, textStyle: {fontSize: 11, color: axisText}, titleTextStyle: {color: axisTitle}},
        legend: "none",
        chartArea: {width: "82%", height: "70%"},
        colors: ["#4DB6AC"],
        backgroundColor: {fill: "transparent"},
        tooltip: {textStyle: {fontSize: 12}, showColorCode: true},
        ...extra,
    };
};

const PIE_COLORS = ["#4DB6AC", "#5C6BC0", "#FFA726", "#EF5350", "#66BB6A", "#42A5F5", "#AB47BC", "#26C6DA"];

const CHART_HEIGHT = 280;

/**
 * Build google-charts rows for a single-series chart, adding the comparison
 * period as a second series when `comparison` ({data, currentRange,
 * previousRange}) is set.
 *
 * - kind "time": day-by-day alignment (day N ↔ day N of the comparison
 *   period); the comparison point's tooltip shows its own date.
 * - kind "category": merged by label (hour, payment method…).
 */
const useSeriesRows = ({series, comparison, toSeries, kind, xLabel, yLabel, formatValue = (v) => v}) => {
    const {t} = useTranslation("activite");
    return useMemo(() => {
        if (!comparison) {
            return {
                rows: [[xLabel, yLabel], ...series.map((p) => [p.label, p.value])],
                compared: false,
                isEmpty: series.length === 0 || series.every((p) => p.value === 0),
            };
        }
        const previousName = t("comparison.previousSeries");
        const previousSeries = toSeries(comparison.data);
        if (kind === "time") {
            const aligned = alignTimeSeries(series, previousSeries, comparison.currentRange, comparison.previousRange);
            return {
                rows: [
                    [xLabel, yLabel, previousName, {type: "string", role: "tooltip"}],
                    ...aligned.map((r) => [
                        r.label,
                        r.value,
                        r.previous,
                        `${r.previousLabel}\n${previousName}: ${formatValue(r.previous)}`,
                    ]),
                ],
                compared: true,
                isEmpty: aligned.length === 0 || aligned.every((r) => r.value === 0 && r.previous === 0),
            };
        }
        const merged = mergeSeriesByLabel(series, previousSeries);
        return {
            rows: [[xLabel, yLabel, previousName], ...merged.map((r) => [r.label, r.value, r.previous])],
            compared: true,
            isEmpty: merged.length === 0 || merged.every((r) => r.value === 0 && r.previous === 0),
        };
    }, [series, comparison, toSeries, kind, xLabel, yLabel, formatValue, t]);
};

// Extra options for a chart showing the comparison series.
const comparisonOptions = (isDarkMode, color, dashed = false) => ({
    colors: [color, COMPARISON_COLOR],
    legend: comparisonLegend(isDarkMode),
    ...(dashed ? {series: dashedComparisonSeries} : {}),
});

const formatCount = (v) => Number(v || 0).toLocaleString("fr-FR");

// ── Daily revenue trend (area) ──────────────────────────────────────────────
export const DailyRevenueTrendChart = ({data, isLoading, isDarkMode, comparison}) => {
    const {t} = useTranslation("activite");
    const def = CHART_BY_ID.daily_revenue_trend;
    const series = useMemo(() => dailyRevenueSeries(data), [data]);
    const {rows, compared, isEmpty} = useSeriesRows({
        series,
        comparison,
        toSeries: dailyRevenueSeries,
        kind: "time",
        xLabel: t("dashboard.columns.date"),
        yLabel: t("dashboard.columns.revenue"),
        formatValue: formatCurrency,
    });
    return (
        <ChartCard
            title={t(def.labelKey)}
            icon={def.icon}
            isLoading={isLoading}
            isEmpty={compared ? isEmpty : series.length === 0}
            isDarkMode={isDarkMode}
        >
            <Chart
                chartType="AreaChart"
                width="100%"
                height={`${CHART_HEIGHT}px`}
                data={rows}
                options={baseOptions(isDarkMode, compared ? comparisonOptions(isDarkMode, "#4DB6AC", true) : {})}
            />
        </ChartCard>
    );
};

// ── Orders trend (line) ─────────────────────────────────────────────────────
export const OrdersTrendChart = ({data, isLoading, isDarkMode, comparison}) => {
    const {t} = useTranslation("activite");
    const def = CHART_BY_ID.orders_trend;
    const series = useMemo(() => dailyOrdersSeries(data), [data]);
    const {rows, compared, isEmpty} = useSeriesRows({
        series,
        comparison,
        toSeries: dailyOrdersSeries,
        kind: "time",
        xLabel: t("dashboard.columns.date"),
        yLabel: t("dashboard.columns.orders"),
        formatValue: formatCount,
    });
    return (
        <ChartCard
            title={t(def.labelKey)}
            icon={def.icon}
            isLoading={isLoading}
            isEmpty={isEmpty}
            isDarkMode={isDarkMode}
        >
            <Chart
                chartType="LineChart"
                width="100%"
                height={`${CHART_HEIGHT}px`}
                data={rows}
                options={baseOptions(isDarkMode, {
                    colors: ["#5C6BC0"],
                    pointSize: 4,
                    ...(compared ? comparisonOptions(isDarkMode, "#5C6BC0", true) : {}),
                })}
            />
        </ChartCard>
    );
};

// ── Payment methods (pie) ───────────────────────────────────────────────────
export const PaymentMethodsChart = ({data, isLoading, isDarkMode, comparison}) => {
    const {t} = useTranslation("activite");
    const def = CHART_BY_ID.payment_methods;
    const series = useMemo(() => paymentMethodsSeries(data), [data]);
    const {rows, compared, isEmpty} = useSeriesRows({
        series,
        comparison,
        toSeries: paymentMethodsSeries,
        kind: "category",
        xLabel: t("dashboard.columns.method"),
        yLabel: t("dashboard.columns.amount"),
    });
    return (
        <ChartCard
            title={t(def.labelKey)}
            icon={def.icon}
            isLoading={isLoading}
            isEmpty={isEmpty}
            isDarkMode={isDarkMode}
        >
            {compared ? (
                // Two pies are hard to compare — grouped columns instead.
                <Chart
                    chartType="ColumnChart"
                    width="100%"
                    height={`${CHART_HEIGHT}px`}
                    data={rows}
                    options={baseOptions(isDarkMode, comparisonOptions(isDarkMode, "#4DB6AC"))}
                />
            ) : (
            <Chart
                chartType="PieChart"
                width="100%"
                height={`${CHART_HEIGHT}px`}
                data={rows}
                options={{
                    pieHole: 0.4,
                    colors: PIE_COLORS,
                    backgroundColor: {fill: "transparent"},
                    legend: {position: "bottom", textStyle: {color: isDarkMode ? "#D1D5DB" : "#374151", fontSize: 11}},
                    chartArea: {width: "90%", height: "75%"},
                    tooltip: {textStyle: {fontSize: 12}},
                }}
            />
            )}
        </ChartCard>
    );
};

// ── Revenue by hour (column) ────────────────────────────────────────────────
export const RevenueByHourChart = ({data, isLoading, isDarkMode, comparison}) => {
    const {t} = useTranslation("activite");
    const def = CHART_BY_ID.revenue_by_hour;
    const series = useMemo(() => revenueByHourSeries(data), [data]);
    const {rows, compared, isEmpty} = useSeriesRows({
        series,
        comparison,
        toSeries: revenueByHourSeries,
        kind: "category",
        xLabel: t("dashboard.columns.hour"),
        yLabel: t("dashboard.columns.revenue"),
    });
    return (
        <ChartCard
            title={t(def.labelKey)}
            icon={def.icon}
            isLoading={isLoading}
            isEmpty={isEmpty}
            isDarkMode={isDarkMode}
        >
            <Chart
                chartType="ColumnChart"
                width="100%"
                height={`${CHART_HEIGHT}px`}
                data={rows}
                options={baseOptions(isDarkMode, compared ? comparisonOptions(isDarkMode, "#42A5F5") : {colors: ["#42A5F5"]})}
            />
        </ChartCard>
    );
};

// ── Revenue by service period (pie) ─────────────────────────────────────────
export const RevenueByPeriodChart = ({data, isLoading, isDarkMode, comparison}) => {
    const {t} = useTranslation("activite");
    const def = CHART_BY_ID.revenue_by_period;
    const series = useMemo(() => revenueByPeriodSeries(data), [data]);
    const {rows, compared, isEmpty} = useSeriesRows({
        series,
        comparison,
        toSeries: revenueByPeriodSeries,
        kind: "category",
        xLabel: t("dashboard.columns.service"),
        yLabel: t("dashboard.columns.revenue"),
    });
    return (
        <ChartCard
            title={t(def.labelKey)}
            icon={def.icon}
            isLoading={isLoading}
            isEmpty={isEmpty}
            isDarkMode={isDarkMode}
        >
            {compared ? (
                // Two pies are hard to compare — grouped columns instead.
                <Chart
                    chartType="ColumnChart"
                    width="100%"
                    height={`${CHART_HEIGHT}px`}
                    data={rows}
                    options={baseOptions(isDarkMode, comparisonOptions(isDarkMode, "#4DB6AC"))}
                />
            ) : (
            <Chart
                chartType="PieChart"
                width="100%"
                height={`${CHART_HEIGHT}px`}
                data={rows}
                options={{
                    colors: PIE_COLORS,
                    backgroundColor: {fill: "transparent"},
                    legend: {position: "bottom", textStyle: {color: isDarkMode ? "#D1D5DB" : "#374151", fontSize: 11}},
                    chartArea: {width: "90%", height: "75%"},
                    tooltip: {textStyle: {fontSize: 12}},
                }}
            />
            )}
        </ChartCard>
    );
};

// ── Activity heatmap (custom grid: weekday × hour) ──────────────────────────
const heatColor = (ratio, isDarkMode) => {
    if (ratio <= 0) return isDarkMode ? "#1F2937" : "#F3F4F6";
    // Interpolate opacity of the primary teal for intensity.
    const alpha = 0.15 + ratio * 0.85;
    return `rgba(77, 182, 172, ${alpha.toFixed(3)})`;
};

export const ActivityHeatmapChart = ({data, isLoading, isDarkMode}) => {
    const {t} = useTranslation("activite");
    const def = CHART_BY_ID.activity_heatmap;
    const matrix = useMemo(() => heatmapMatrix(data), [data]);

    const {weekdays, hours, lookup, max} = useMemo(() => {
        const wd = [];
        const hrs = new Set();
        const map = {};
        let mx = 0;
        matrix.forEach(({weekday, hour, value}) => {
            const wkey = String(weekday);
            if (!wd.includes(wkey)) wd.push(wkey);
            hrs.add(hour);
            map[`${wkey}|${hour}`] = value;
            if (value > mx) mx = value;
        });
        return {
            weekdays: wd,
            hours: [...hrs].sort((a, b) => a - b),
            lookup: map,
            max: mx,
        };
    }, [matrix]);

    const isEmpty = matrix.length === 0 || max === 0;

    return (
        <ChartCard
            title={t(def.labelKey)}
            icon={def.icon}
            isLoading={isLoading}
            isEmpty={isEmpty}
            isDarkMode={isDarkMode}
            minHeight={220}
        >
            <div className="overflow-x-auto">
                <table className="border-separate" style={{borderSpacing: "3px"}}>
                    <thead>
                        <tr>
                            <th />
                            {hours.map((h) => (
                                <th
                                    key={h}
                                    className={`px-1 text-[10px] font-normal ${
                                        isDarkMode ? "text-gray-500" : "text-gray-400"
                                    }`}
                                >
                                    {h}h
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {weekdays.map((wd) => (
                            <tr key={wd}>
                                <td
                                    className={`pr-2 text-right text-[11px] font-medium capitalize ${
                                        isDarkMode ? "text-gray-300" : "text-gray-600"
                                    }`}
                                >
                                    {wd}
                                </td>
                                {hours.map((h) => {
                                    const value = lookup[`${wd}|${h}`] || 0;
                                    const ratio = max > 0 ? value / max : 0;
                                    return (
                                        <td key={h}>
                                            <div
                                                title={`${wd} ${h}h — ${formatCurrency(value)}`}
                                                className="h-6 w-6 rounded"
                                                style={{backgroundColor: heatColor(ratio, isDarkMode)}}
                                            />
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </ChartCard>
    );
};

// ── New vs returning clients (grouped column) ───────────────────────────────
export const NewVsReturningChart = ({data, isLoading, isDarkMode, comparison}) => {
    const {t} = useTranslation("activite");
    const def = CHART_BY_ID.new_vs_returning;
    const split = useMemo(() => newVsReturning(data), [data]);
    const previousSplit = useMemo(
        () => (comparison ? newVsReturning(comparison.data) : null),
        [comparison]
    );
    const compared = !!comparison;
    const rows = useMemo(() => {
        if (!split) return [];
        if (compared) {
            // Each metric is followed by its comparison-period value.
            const prev = previousSplit || {new: {clients: 0, orders: 0}, returning: {clients: 0, orders: 0}};
            const suffix = ` (${t("comparison.previousShort")})`;
            return [
                [
                    t("dashboard.columns.type"),
                    t("dashboard.columns.clients"),
                    t("dashboard.columns.clients") + suffix,
                    t("dashboard.columns.orders"),
                    t("dashboard.columns.orders") + suffix,
                ],
                [t("dashboard.columns.newClients"), split.new.clients, prev.new.clients, split.new.orders, prev.new.orders],
                [t("dashboard.columns.returningClients"), split.returning.clients, prev.returning.clients, split.returning.orders, prev.returning.orders],
            ];
        }
        return [
            [t("dashboard.columns.type"), t("dashboard.columns.clients"), t("dashboard.columns.orders")],
            [t("dashboard.columns.newClients"), split.new.clients, split.new.orders],
            [t("dashboard.columns.returningClients"), split.returning.clients, split.returning.orders],
        ];
    }, [split, previousSplit, compared, t]);
    const isEmpty =
        !split ||
        split.new.clients + split.returning.clients + split.new.orders + split.returning.orders === 0;
    return (
        <ChartCard
            title={t(def.labelKey)}
            icon={def.icon}
            isLoading={isLoading}
            isEmpty={isEmpty}
            isDarkMode={isDarkMode}
        >
            <Chart
                chartType="ColumnChart"
                width="100%"
                height={`${CHART_HEIGHT}px`}
                data={rows}
                options={baseOptions(isDarkMode, {
                    // Comparison columns use lighter tints of the same colours.
                    colors: compared ? ["#4DB6AC", "#B2DFDB", "#5C6BC0", "#C5CAE9"] : ["#4DB6AC", "#5C6BC0"],
                    legend: {position: "bottom", textStyle: {color: isDarkMode ? "#D1D5DB" : "#374151", fontSize: 11}},
                })}
            />
        </ChartCard>
    );
};

// ── Order frequency distribution (column) ───────────────────────────────────
export const FrequencyDistributionChart = ({data, isLoading, isDarkMode, comparison}) => {
    const {t} = useTranslation("activite");
    const def = CHART_BY_ID.frequency_distribution;
    const series = useMemo(() => frequencyDistributionSeries(data), [data]);
    const {rows, compared, isEmpty} = useSeriesRows({
        series,
        comparison,
        toSeries: frequencyDistributionSeries,
        kind: "category",
        xLabel: t("dashboard.columns.frequency"),
        yLabel: t("dashboard.columns.clients"),
    });
    return (
        <ChartCard
            title={t(def.labelKey)}
            icon={def.icon}
            isLoading={isLoading}
            isEmpty={isEmpty}
            isDarkMode={isDarkMode}
        >
            <Chart
                chartType="ColumnChart"
                width="100%"
                height={`${CHART_HEIGHT}px`}
                data={rows}
                options={baseOptions(isDarkMode, compared ? comparisonOptions(isDarkMode, "#7E57C2") : {colors: ["#7E57C2"]})}
            />
        </ChartCard>
    );
};

// ── Top clients (table) ─────────────────────────────────────────────────────
export const TopClientsWidget = ({data, isLoading, isDarkMode}) => {
    const {t} = useTranslation("activite");
    const def = CHART_BY_ID.top_clients;
    const rows = useMemo(() => topClientsRows(data), [data]);
    const isEmpty = rows.length === 0;

    const headCls = `px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide ${
        isDarkMode ? "text-gray-400" : "text-gray-500"
    }`;
    const cellCls = `px-3 py-2 text-xs ${isDarkMode ? "text-gray-200" : "text-gray-700"}`;

    return (
        <ChartCard
            title={t(def.labelKey)}
            icon={def.icon}
            isLoading={isLoading}
            isEmpty={isEmpty}
            isDarkMode={isDarkMode}
            minHeight={200}
        >
            <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                    <thead>
                        <tr className={`border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
                            <th className={headCls}>{t("dashboard.columns.client")}</th>
                            <th className={`${headCls} text-right`}>{t("dashboard.columns.orders")}</th>
                            <th className={`${headCls} text-right`}>{t("dashboard.columns.revenue")}</th>
                            <th className={`${headCls} text-right`}>{t("dashboard.columns.averageOrder")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((c, i) => (
                            <tr
                                key={c.id || i}
                                className={`border-b last:border-0 ${
                                    isDarkMode ? "border-gray-700/60 hover:bg-gray-700/30" : "border-gray-100 hover:bg-gray-50"
                                }`}
                            >
                                <td className={cellCls}>
                                    <div className="flex items-center gap-2">
                                        <span
                                            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary"
                                        >
                                            {i + 1}
                                        </span>
                                        <div className="min-w-0">
                                            <div className="truncate font-medium">{c.name}</div>
                                            {c.phone && (
                                                <div className={`truncate text-[10px] ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                                                    {c.phone}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className={`${cellCls} text-right`}>{c.orders}</td>
                                <td className={`${cellCls} text-right font-semibold text-primary`}>
                                    {formatCurrency(c.revenue)}
                                </td>
                                <td className={`${cellCls} text-right`}>{formatCurrency(c.aov)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </ChartCard>
    );
};

// Map chart id → component for the dashboard renderer.
export const CHART_COMPONENTS = {
    daily_revenue_trend: DailyRevenueTrendChart,
    orders_trend: OrdersTrendChart,
    payment_methods: PaymentMethodsChart,
    revenue_by_hour: RevenueByHourChart,
    revenue_by_period: RevenueByPeriodChart,
    activity_heatmap: ActivityHeatmapChart,
    new_vs_returning: NewVsReturningChart,
    frequency_distribution: FrequencyDistributionChart,
    top_clients: TopClientsWidget,
};
